import React from 'react';
import { Box, Button, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { OP_LABEL, RELATIVE_OPTIONS } from '../../utils/reportSelection';

const MAX_FILTERS = 10;

const isDateType = (dataTyp) => dataTyp === 'DATE' || dataTyp === 'DTM';

/** 조건 줄 - 필드 / 연산자 / 값. 연산자는 필드의 allowedOps(서버가 타입별로 내려준 값)만 고른다. */
const FilterBuilder = ({ fields = [], filters = [], onFiltersChange }) => {
  const byCode = Object.fromEntries(fields.map((f) => [f.fieldCd, f]));
  const filterableFields = fields.filter((f) => f.filterable);

  const update = (idx, patch) => {
    const next = filters.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onFiltersChange(next);
  };

  const addRow = () => {
    if (filters.length >= MAX_FILTERS) return;
    const first = filterableFields[0];
    onFiltersChange([...filters, { field: first?.fieldCd || '', op: first?.allowedOps?.[0] || 'EQ' }]);
  };

  const removeRow = (idx) => onFiltersChange(filters.filter((_, i) => i !== idx));

  const renderValueInputs = (row, idx) => {
    const field = byCode[row.field];
    const typ = field?.dataTyp;
    const inputType = isDateType(typ) ? 'date' : typ === 'NUM' || typ === 'MONEY' ? 'number' : 'text';

    // 7번(Fable 재검토 2026-10-26): 상대 기간 - 코드 선택(+최근 N일일 때만 숫자 입력).
    if (row.op === 'RELATIVE') {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Select
            size="small"
            displayEmpty
            value={row.value || ''}
            onChange={(e) => update(idx, { value: e.target.value })}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="" disabled>기간 고르기</MenuItem>
            {RELATIVE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
          {row.value === 'LAST_N_DAYS' && (
            <TextField
              size="small"
              type="number"
              label="N일"
              value={row.value1 || ''}
              onChange={(e) => update(idx, { value1: e.target.value })}
              sx={{ width: 90 }}
            />
          )}
        </Box>
      );
    }

    // 8번(Fable 재검토 2026-10-26): CODE 필드는 자유 입력 대신 서버가 내려준 공통코드/고정 목록
    // 드롭다운으로 - 이 줄은 IN/BETWEEN이 아닐 때(=/≠)만 적용한다(IN은 쉼표 목록 입력을 유지).
    if (field?.dataTyp === 'CODE' && field.codeOptions?.length > 0 && row.op !== 'IN' && row.op !== 'BETWEEN') {
      return (
        <Select
          size="small"
          displayEmpty
          value={row.value || ''}
          onChange={(e) => update(idx, { value: e.target.value })}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="" disabled>값 고르기</MenuItem>
          {field.codeOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
      );
    }

    if (row.op === 'IN') {
      return (
        <TextField
          size="small"
          label="값(쉼표로 구분)"
          value={(row.values || []).join(',')}
          onChange={(e) => update(idx, { values: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })}
          sx={{ minWidth: 180 }}
        />
      );
    }
    if (row.op === 'BETWEEN') {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" type={inputType} label="부터" value={row.value1 || ''} onChange={(e) => update(idx, { value1: e.target.value })} InputLabelProps={inputType === 'date' ? { shrink: true } : undefined} />
          <TextField size="small" type={inputType} label="까지" value={row.value2 || ''} onChange={(e) => update(idx, { value2: e.target.value })} InputLabelProps={inputType === 'date' ? { shrink: true } : undefined} />
        </Box>
      );
    }
    return (
      <TextField
        size="small"
        type={inputType}
        label="값"
        value={row.value || ''}
        onChange={(e) => update(idx, { value: e.target.value })}
        InputLabelProps={inputType === 'date' ? { shrink: true } : undefined}
        sx={{ minWidth: 140 }}
      />
    );
  };

  return (
    <Box>
      {filters.map((row, idx) => (
        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Select
            size="small"
            value={row.field || ''}
            onChange={(e) => {
              const f = byCode[e.target.value];
              update(idx, { field: e.target.value, op: f?.allowedOps?.[0] || 'EQ', value: '', value1: '', value2: '', values: [] });
            }}
            sx={{ minWidth: 150 }}
          >
            {filterableFields.map((f) => (
              <MenuItem key={f.fieldCd} value={f.fieldCd}>{f.fieldNm}</MenuItem>
            ))}
          </Select>
          <Select size="small" value={row.op || ''} onChange={(e) => update(idx, { op: e.target.value })} sx={{ minWidth: 110 }}>
            {(byCode[row.field]?.allowedOps || []).map((op) => (
              <MenuItem key={op} value={op}>{OP_LABEL[op] || op}</MenuItem>
            ))}
          </Select>
          {renderValueInputs(row, idx)}
          <IconButton size="small" onClick={() => removeRow(idx)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      {filters.length === 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>조건 없음(전체)</Typography>
      )}
      <Button size="small" startIcon={<AddIcon />} onClick={addRow} disabled={filters.length >= MAX_FILTERS || filterableFields.length === 0}>
        조건 추가
      </Button>
    </Box>
  );
};

export default FilterBuilder;
